import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import styles from './ui.module.css';

const UiContext = createContext(null);

export const useUi = () => {
    const ctx = useContext(UiContext);
    if (!ctx) throw new Error('useUi должен вызываться внутри UiProvider');
    return ctx;
};

let nextId = 1;

/**
 * Тосты и диалог подтверждения.
 *
 * Заменяет связку alert() + window.location.reload(), которая стояла после каждого
 * действия: страница перезагружалась, теряя фильтры и позицию скролла, а удаление
 * вообще происходило без единого вопроса.
 */
export const UiProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const [confirmState, setConfirmState] = useState(null);
    const resolverRef = useRef(null);

    const dismiss = useCallback((id) => {
        setToasts((current) => current.filter((t) => t.id !== id));
    }, []);

    const pushToast = useCallback(
        (type, message, { timeout = 5000 } = {}) => {
            const id = nextId++;
            setToasts((current) => [...current, { id, type, message }]);
            if (timeout) {
                setTimeout(() => dismiss(id), timeout);
            }
            return id;
        },
        [dismiss]
    );

    const toast = useMemo(
        () => ({
            success: (message, opts) => pushToast('success', message, opts),
            error: (message, opts) => pushToast('error', message, { timeout: 8000, ...opts }),
            info: (message, opts) => pushToast('info', message, opts),
        }),
        [pushToast]
    );

    /** Возвращает промис, который резолвится в true/false. */
    const confirm = useCallback((options) => {
        setConfirmState({
            title: 'Подтвердите действие',
            confirmLabel: 'Подтвердить',
            cancelLabel: 'Отмена',
            danger: false,
            ...options,
        });

        return new Promise((resolve) => {
            resolverRef.current = resolve;
        });
    }, []);

    const closeConfirm = useCallback((result) => {
        setConfirmState(null);
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    const value = useMemo(() => ({ toast, confirm }), [toast, confirm]);

    return (
        <UiContext.Provider value={value}>
            {children}

            <div className={styles.toasts} role="status" aria-live="polite">
                {toasts.map((t) => (
                    <div key={t.id} className={`${styles.toast} ${styles[t.type]}`}>
                        <span className={styles.toastText}>{t.message}</span>
                        <button
                            type="button"
                            className={styles.toastClose}
                            onClick={() => dismiss(t.id)}
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>

            {confirmState && (
                <div className={styles.backdrop} onClick={() => closeConfirm(false)}>
                    <div
                        className={styles.dialog}
                        role="dialog"
                        aria-modal="true"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className={styles.dialogTitle}>{confirmState.title}</h3>
                        {confirmState.message && (
                            <p className={styles.dialogMessage}>{confirmState.message}</p>
                        )}
                        <div className={styles.dialogActions}>
                            <button
                                type="button"
                                className={styles.buttonGhost}
                                onClick={() => closeConfirm(false)}
                            >
                                {confirmState.cancelLabel}
                            </button>
                            <button
                                type="button"
                                className={confirmState.danger ? styles.buttonDanger : styles.buttonPrimary}
                                onClick={() => closeConfirm(true)}
                                autoFocus
                            >
                                {confirmState.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </UiContext.Provider>
    );
};

export default UiContext;
